<template>
  <div id="app" class="h-100" :class="[skinClasses]">
    <component :is="layout">
      <router-view />
    </component>

    <scroll-to-top v-if="enableScrollToTop" />

    <!-- Popup de último acesso -->
    <last-access-popup
      v-model="showLastAccessPopup"
      :last-access="lastAccessData"
    />

    <session-timeout-renew-popup />
    <session-timeout-expired-popup />
  </div>
</template>

<script>
import Echo from 'laravel-echo'
import ScrollToTop from '@core/components/scroll-to-top/ScrollToTop.vue'
import LastAccessPopup from '@/components/LastAccessPopup.vue'
import SessionTimeoutRenewPopup from '@/components/SessionTimeoutRenewPopup.vue'
import SessionTimeoutExpiredPopup from '@/components/SessionTimeoutExpiredPopup.vue'
import sessionTimeoutService from '@/services/SessionTimeoutService'
import { isUserLoggedIn } from '@/util/Util'

// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig'
import { provideToast } from 'vue-toastification/composition'
import { watch, ref } from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'

import { useWindowSize, useCssVar } from '@vueuse/core'

import { moment_ptbr } from './i18n/moment'

import store from '@/store/store'

const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue')
const LayoutHorizontal = () =>
  import('@/layouts/horizontal/LayoutHorizontal.vue')
const LayoutFull = () => import('@/layouts/full/LayoutFull.vue')

export default {
  components: {
    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,

    ScrollToTop,
    LastAccessPopup,
    SessionTimeoutRenewPopup,
    SessionTimeoutExpiredPopup
  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full'
      return `layout-${this.contentLayoutType}`
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type
    },
  },
  methods: {
    preventAutofill() {
      const inputs = [
        ...document.querySelectorAll(
          'form[autocomplete="off"] input:not([autocomplete])'
        )
      ]
      if (inputs.length > 0) {
        inputs.forEach(input => {
          input.setAttribute('autocomplete', 'off')
        })
      }
    },
    setupSessionTimeout() {
      sessionTimeoutService.onExpire(() => {
        this.handleSessionExpired()
      })
      this.syncSessionTimeout()
    },
    syncSessionTimeout() {
      if (sessionTimeoutService.isExpiredPromptActive()) return

      if (isUserLoggedIn() && sessionTimeoutService.isEnabled()) {
        sessionTimeoutService.start()
        return
      }

      sessionTimeoutService.stop()
    },
    handleSessionExpired() {
      this.closeLastAccess()
    },
    closeLastAccess() {
      this.showLastAccessPopup = false
      this.lastAccessData = null
    },
    clearAuthOverlays() {
      this.closeLastAccess()
      this.$nextTick(() => {
        document.body.classList.remove('modal-open')
        document.querySelectorAll('.modal-backdrop').forEach(backdrop => backdrop.remove())
      })
    }
  },
  watch: {
    // Important Echo Instance Refresher
    '$store.getters.userToken'(val) {
      if (this._.has(window, 'Echo')) {
        /**
         * 4-step refresh process (E - happens if token is empty)
         * 1 - Leave all channels       (E)
         * 2 - Disconnect               (E)
         * 3 - Change token in options  (*)
         * 4 - Reconnect                (!E)
         */

        // 1. Echo leave channels if empty token.
        if (this.isEmpty(val)) {
          const channels = Object.keys(window.Echo.connector.channels)
          channels.forEach(channel => {
            window.Echo.leaveChannel(channel)
          })
        }

        // 2. Echo disconnect if empty token.
        if (this.isEmpty(val)) {
          window.Echo.disconnect()
        }

        // 3. Echo change option token.
        if (this._.has(window, 'Echo.options.auth.headers')) {
          console.log('Echo instance Authorization Header updated.')
          window.Echo.options.auth.headers.Authorization = `Bearer ${val}`
        }

        // 4. Echo re-connect if not empty token.
        if (!this.isEmpty(val)) {
          window.Echo.connect()
        }
      }

      this.syncSessionTimeout()
    },
    '$route.path'(path) {
      if (path === '/login' || path === '/a2fa') {
        this.clearAuthOverlays()
      }
    }
  },
  beforeCreate() {
    // Set colors in theme
    const colors = [
      'primary',
      'secondary',
      'success',
      'info',
      'warning',
      'danger',
      'light',
      'dark'
    ]

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(
        `--${colors[i]}`,
        document.documentElement
      ).value.trim()
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(
        useCssVar(
          `--breakpoint-${breakpoints[i]}`,
          document.documentElement
        ).value.slice(0, -2)
      )
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')

    if (process.env.VUE_APP_SOCKET_CONNECT) {
      window.Pusher = require('pusher-js')

      window.Echo = new Echo({
        broadcaster: 'pusher',
        key: process.env.VUE_APP_PUSHER_KEY,
        wsHost: process.env.VUE_APP_PUSHER_HOST,
        authEndpoint: `${process.env.VUE_APP_API_BASE_URL}/broadcasting/auth`,
        encrypted: true,
        forceTLS: true,
        wsPort: 6001,
        wssPort: 6001,
        disableStats: true,
        enabledTransports: ['ws', 'wss'],
        auth: {
          headers: {
            Authorization: `Bearer ${this.$store.getters.userToken}`,
            'Access-Control-Allow-Origin': '*',
            'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS',
            'Access-Control-Allow-Headers':
              'Origin, Content-Type, Accept, Authorization, X-Requested-With, X-CSRF-Token'
          }
        }
      })
    }
  },
  data() {
    return {
      showLastAccessPopup: false,
      lastAccessData: null
    }
  },
  setup() {
    const { skin, skinClasses } = useAppConfig()
    const { enableScrollToTop } = $themeConfig.layout

    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout')

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: 'Vue-Toastification__fade'
    })

    // Set Window Width in store
    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    const { width: windowWidth } = useWindowSize()
    watch(windowWidth, val => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val)
    })

    return {
      skinClasses,
      enableScrollToTop
    }
  },
  mounted() {
    this.$moment.defineLocale('pt-br', moment_ptbr)
    setInterval(this.preventAutofill, 1000)

    // Armazena instância do Vue para acesso global
    window.__vueAppInstance__ = this

    // Escuta evento para mostrar popup de último acesso
    this.$root.$on('showLastAccessPopup', data => {
      if (this.$route.path === '/login' || this.$route.path === '/a2fa') return
      if (!isUserLoggedIn()) return
      this.lastAccessData = data
      this.$nextTick(() => {
        this.showLastAccessPopup = true
      })
    })
    this.$root.$on('hideLastAccessPopup', () => {
      this.clearAuthOverlays()
    })

    this.setupSessionTimeout()
  },
  beforeDestroy() {
    sessionTimeoutService.stop()
  }
}
</script>
